import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { Helmet } from "react-helmet";
import MenuNav from "../components/Menunav";
import MobileMenu from "../components/MobileMenu";
import WhatsAppButton from "../components/WhatsAppButton";
import { FiHeart, FiShare2 } from "react-icons/fi"; // icônes ligne simple
import { useAuth } from "../hooks/useAuth";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  where
} from "firebase/firestore";

export default function VehicleDetail() {
  const [vehicle, setVehicle] = useState(null);
  const [mainImage, setMainImage] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [show360Modal, setShow360Modal] = useState(false);
  const { slug } = useParams();
  const { user } = useAuth(); // doit contenir user.uid
  const [isFavori, setIsFavori] = useState(false);
  const [favoriId, setFavoriId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [similarVehicles, setSimilarVehicles] = useState([]); // state pour les véhicules similaires

  useEffect(() => {
    if (!user || !vehicle) return; // ✅ on attend que vehicle soit non null
    const checkFavori = async () => {
      const q = query(
        collection(db, "favoris"),
        where("userId", "==", user.uid),
        where("vehicleId", "==", vehicle.slug)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setIsFavori(true);
        setFavoriId(snapshot.docs[0].id);
      }
    };

    checkFavori();
  }, [user, vehicle]);

  const toggleFavori = async () => {
    if (!user) {
      // Rediriger vers connexion avec retour prévu
      navigate("/Identity/Account/Login", { state: { from: location.pathname } });
      return;
    }

    if (!vehicle || !vehicle.slug) {
      alert("Le véhicule est introuvable.");
      return;
    }

    try {
      if (isFavori && favoriId) {
        await deleteDoc(doc(db, "favoris", favoriId));
        setIsFavori(false);
        setFavoriId(null);
      } else {
        const docRef = await addDoc(collection(db, "favoris"), {
          userId: user.uid,
          vehicleId: vehicle.slug, // ✅ stocke le slug
          createdAt: new Date(),
        });
        setIsFavori(true);
        setFavoriId(docRef.id);
      }
    } catch (error) {
      console.error("Erreur favoris :", error);
    }
  };

  useEffect(() => {
    const fetchVehicleBySlug = async () => {
      const q = query(
        collection(db, "vehicules_auto"),
        where("slug", "==", slug) // slug est une variable passée en paramètre
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const docSnap = querySnapshot.docs[0];
        const data = docSnap.data();
        setVehicle(data);
        if (data.imageUrls?.length) setMainImage(data.imageUrls[0]);
      }
    };

    if (slug) fetchVehicleBySlug();
  }, [slug]);

  // Récupérer les véhicules similaires
  useEffect(() => {
    const fetchSimilarVehicles = async () => {
      if (!vehicle) return; // Vérifie si le véhicule est chargé
      const q = query(
        collection(db, "vehicules_auto"),
        where("marque", "==", vehicle.marque),
        where("slug", "!=" , vehicle.slug) // Exclure le véhicule actuel
      );
      const querySnapshot = await getDocs(q);
      const similarVehiclesData = querySnapshot.docs.map((doc) => doc.data());
      setSimilarVehicles(similarVehiclesData);
    };

    fetchSimilarVehicles();
  }, [vehicle]);

  if (!vehicle) return <div className="text-center py-5">Chargement...</div>;

  const handleShare = () => {
    const shareUrl = window.location.href;

    if (navigator.share) {
      navigator
        .share({
          title: "Véhicule à vendre",
          text: "Regarde ce véhicule intéressant !",
          url: shareUrl,
        })
        .catch((err) => console.log("Erreur partage :", err));
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert("Lien copié dans le presse-papiers !");
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {`${vehicle.annee} ${vehicle.marque} ${vehicle.modele} ${vehicle.location?.estDisponible && vehicle.enVente
            ? "à vendre et à louer"
            : vehicle.location?.estDisponible
            ? "à louer"
            : "à vendre"} | TheBridgeOfWorld.com`}
        </title>
        <meta
          name="description"
          content={`Achetez une ${vehicle.marque} ${vehicle.modele} ${vehicle.annee} – ${vehicle.ficheTechnique?.carburant || "Essence ou Diesel"}, ${vehicle.ficheTechnique?.transmission || "Transmission manuelle ou automatique"} – Disponible chez The Bridge of World.`}
        />
        <meta name="keywords" content={`The Bridge | voiture ${vehicle.marque}, ${vehicle.modele}, véhicule occasion ${vehicle.annee}, voiture importée, achat véhicule ${vehicle.marque} ${vehicle.modele}`} />
        <meta name="author" content="The Bridge of World" />
        {/* Open Graph pour les réseaux sociaux */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://thebridgeofworld.com/vehicules/${vehicle.slug}`} />
        <meta property="og:title" content={`${vehicle.marque} ${vehicle.modele} ${vehicle.annee}`} />
        <meta
          property="og:description"
          content={`${vehicle.annee} • ${vehicle.ficheTechnique?.carburant || "Essence ou Diesel"} • ${vehicle.ficheTechnique?.transmission || "Transmission automatique/manuelle"}`}
        />
        <meta property="og:image" content={vehicle.imageUrls?.[0]} />
        {/* Schema.org Product pour Google Rich Results */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": `${vehicle.marque} ${vehicle.modele} ${vehicle.annee}`,
            "image": vehicle.imageUrls || [],
            "description": `Véhicule ${vehicle.marque} ${vehicle.modele} de ${vehicle.annee}, ${vehicle.ficheTechnique?.carburant}, ${vehicle.ficheTechnique?.transmission}`,
            "brand": {
              "@type": "Brand",
              "name": vehicle.marque,
            },
            "offers": {
              "@type": "Offer",
              "price": vehicle.prix || "À discuter",
              "priceCurrency": "XOF",
              "availability": "https://schema.org/InStock",
              "url": `https://thebridgeofworld.com/vehicules/${vehicle.slug}`,
              "itemCondition": "https://schema.org/UsedCondition",
            },
          })}
        </script>
      </Helmet>

      <MenuNav />
      <MobileMenu />
      <main>
        <div className="container py-5">
          
          <h1 className="text-center fw-bold text-dark mb-4">
            {`${vehicle.marque} ${vehicle.modele} ${vehicle.annee} `}
            {vehicle.location?.estDisponible && vehicle.enVente
              ? "à vendre et à louer"
              : vehicle.location?.estDisponible
                ? "à louer"
                : "à vendre"}
          </h1>

          {/* <h2 className="text-center fw-bold text-dark mb-4">{vehicle.annee} {vehicle.marque} {vehicle.modele}</h2> */}

          <div className="row mb-4">
            <div className="col-lg-6">
              <div className="position-relative border rounded overflow-hidden mb-2">
                <button
                  className="position-absolute top-0 end-0 m-2 bg-white border-0 rounded-circle shadow"
                  style={{ width: "38px", height: "38px", display: "flex", alignItems: "center", justifyContent: "center" }}
                  onClick={toggleFavori}
                  title={isFavori ? "Retirer des favoris" : "Ajouter aux favoris"}
                >
                  <FiHeart size={18} color={isFavori ? "red" : "gray"} />
                </button>

                {/* <FiHeart size={18} color={isFavori ? "red" : "black"} /> */}
                {mainImage === vehicle.videoUrl ? (
                  <video controls className="w-100" style={{ maxHeight: "400px", objectFit: "contain" }}>
                    <source src={mainImage} type="video/mp4" />
                  </video>
                ) : (

                  // <img
                  //   loading="lazy"
                  //   src={mainImage}
                  //   alt="Véhicule principal"
                  //   className="w-100"
                  //   style={{ maxHeight: "400px", objectFit: "contain" }}
                  // />
                  <div className="position-relative">
                    <img
                      loading="lazy"
                      src={mainImage}
                      alt="Véhicule principal"
                      className="w-100"
                      style={{ maxHeight: "400px", objectFit: "contain" }}
                    />

                    {parseInt(vehicle.prix) > 0 ? (
                      <span className="badge bg-success position-absolute top-0 end-0 me-5 m-2 shadow" style={{fontSize: '15px'}}>
                        A partir de {parseInt(vehicle.prix).toLocaleString("fr-FR")} FCFA
                      </span>
                    ) : (
                      <span className="badge bg-warning position-absolute top-0 end-0 me-5 m-2 shadow" style={{ color: "#0C1E47",fontSize: '20px' }}>
                        Prix à la demande
                      </span>
                    )}


                  </div>

                )}

                {mainImage?.toLowerCase().includes("360") && (
                  <div
                    className="bg-dark text-white text-center d-block"
                    style={{ fontSize: "0.7rem", padding: "3px 0", cursor: "pointer" }}
                    onClick={() => setShow360Modal(true)}
                  >
                    <i className="bi bi-arrow-repeat me-1"></i> 360° View
                  </div>
                )}

                {vehicle.videoUrl && (
                  <span
                    className="bg-dark text-white w-100 text-center d-block"
                    style={{ fontSize: "0.7rem", padding: "2px 0", cursor: "pointer", height: "35px" }}
                    onClick={() => setShowVideoModal(true)}
                  >
                    <i className="bi bi-camera-reels-fill me-1"></i> Engine Starts Video
                  </span>
                )}
              </div>
              <div className="d-flex flex-wrap gap-2">
                {vehicle.imageUrls?.map((url, idx) => (
                  <img
                    loading="lazy"
                    key={idx}
                    src={url}
                    alt={`Aperçu ${idx}`}
                    style={{ width: "112px", height: "75px", cursor: "pointer", border: url === mainImage ? "2px solid red" : "1px solid #ccc" }}
                    onClick={() => setMainImage(url)}
                  />
                ))}
                {vehicle.videoUrl && (
                  <div
                    className="d-flex justify-content-center align-items-center border"
                    style={{ width: "115px", height: "75px", cursor: "pointer" }}
                    onClick={() => setShowVideoModal(true)}
                  >
                    <img
                      loading="lazy"
                      src="/assets/thumbnail-engine-video.jpg"
                      alt="Vidéo"
                      style={{ width: "50px" }}
                    />
                  </div>
                )}
              </div>
              <div className="d-flex flex-column align-items-start gap-2">
                {/* Boutons Like + Share */}
                {/* <div className="d-flex gap-2">
                  <button style={styles.button} onClick={toggleFavori} disabled={!vehicle}>
                    <FiHeart size={18} color={isFavori ? "red" : "black"} />
                  </button>

                  <button style={styles.button} onClick={handleShare}>
                    <FiShare2 size={18} />
                  </button>
                </div> */}
                <div
                  className="d-flex gap-3 p-2 rounded shadow-sm"
                  style={{
                    backgroundColor: "#f1f5f9", // fond gris bleuté doux
                    border: "1px solid #dee2e6", // bordure fine grise
                    alignItems: "center",
                    justifyContent: "start",
                    maxWidth: "180px",
                  }}
                >
                  <button
                    onClick={toggleFavori}
                    disabled={!vehicle}
                    title="Ajouter aux favoris"
                    className="btn btn-light border shadow-sm"
                    style={{
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                    }}
                  >
                    <FiHeart size={20} color={isFavori ? "red" : "#0C1E47"} />
                  </button>

                  <button
                    onClick={handleShare}
                    title="Partager"
                    className="btn btn-light border shadow-sm"
                    style={{
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                    }}
                  >
                    <FiShare2 size={20} color="#0C1E47" />
                  </button>
                </div>

                {/* Prix en dessous */}
                {/* <p className="fw-bold mb-0 border-bottom pb-1">
                  {parseInt(vehicle.prix) > 0 ? (
                    <>
                   
                        A partir de 
                      {parseInt(vehicle.prix).toLocaleString("fr-FR")}{" "}
                      <span className="text-muted fw-semibold" style={{ fontSize: "0.9rem" }}>
                        FCFA
                      </span>
                    </>
                  ) : (
                    <span className="text-dark">Prix à la demande</span>
                  )}
                </p> */}
                <p
                  className="fw-bold mb-3 px-3 py-2 rounded shadow-sm"
                  style={{
                    backgroundColor: "#f8f9fa", // léger fond gris clair
                    borderLeft: "5px solid #F7931E", // bord gauche orange
                    fontSize: "1.25rem", // texte plus gros
                    color: parseInt(vehicle.prix) > 0 ? "#0C1E47" : "#F7931E", // texte sombre ou gris
                  }}
                >
                  {parseInt(vehicle.prix) > 0 ? (
                    <>
                     <span className="fw-semibold" style={{ fontSize: "1rem", color: "#0C1E47" }}>A partir de </span>
                      {parseInt(vehicle.prix).toLocaleString("fr-FR")}{" "}
                      <span className="fw-semibold" style={{ fontSize: "1rem", color: "#0C1E47" }}>
                        FCFA
                      </span>
                    </>
                  ) : (
                    <span>Prix à la demande</span>
                  )}
                </p>
              </div>
            </div>


            <div className="col-lg-6">
              <div className="border rounded shadow-sm mb-4 p-3 bg-white">

                <h5 className="fw-bold border-bottom pb-2">🚘 Informations du Véhicule</h5>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Année :</strong> {vehicle.annee}</p>
                    <p><strong>Marque :</strong> {vehicle.marque}</p>
                    <p><strong>Modèle :</strong> {vehicle.modele}</p>
                    <p><strong>Transmission :</strong> {vehicle.ficheTechnique?.transmission}</p>
                    <p><strong>Carburant :</strong> {vehicle.ficheTechnique?.carburant}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Puissance :</strong> {vehicle.ficheTechnique?.puissance} ch</p>
                    <p><strong>CO₂ :</strong> {vehicle.ficheTechnique?.emissionCO2} g/km</p>
                    <p><strong>Odomètre :</strong> {vehicle.odometre || 'Non précisé'}</p>
                    <p><strong>Clé :</strong> {vehicle.cle ? 'Présente' : 'Présente'}</p>
                    <p><strong>Airbags :</strong> {vehicle.airbags || 'Non précisé'}</p>
                  </div>
                </div>
              </div>

              <div className="border rounded shadow-sm p-3 bg-white">
                <h5 className="fw-bold border-bottom pb-2">📄 Description Complète</h5>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>VIN :</strong> {vehicle.vin || 'Non disponible'}</p>
                    <p><strong>Style :</strong> {vehicle.ficheTechnique?.style || 'Non spécifié'}</p>
                    <p><strong>Poids :</strong> {vehicle.ficheTechnique?.poids} kg</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Coffre :</strong> {vehicle.ficheTechnique?.volumeCoffre} L</p>
                    <p><strong>Norme Euro :</strong> {vehicle.ficheTechnique?.normeEuro}</p>
                    <p><strong>Portes :</strong> {vehicle.ficheTechnique?.portes}</p>
                    <p><strong>Places :</strong> {vehicle.ficheTechnique?.places}</p>
                  </div>
                </div>
              </div>

              <div className="text-center mt-4">
                <a
                  href={`https://wa.me/22878815494?text=Bonjour,%20je%20suis%20intéressé%20par%20le%20${vehicle.marque}%20${vehicle.modele}%20de%20${vehicle.annee}.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-success btn-bridge"
                >
                  <i className="bi bi-whatsapp me-2"></i> Commander via WhatsApp
                </a>
              </div>

            </div>
          </div>
          {/* Afficher les véhicules similaires */}
          <div className="container py-5">
            <h2 className="text-center mb-4">Véhicules Similaires</h2>
            <div className="row">
              {similarVehicles.length > 0 ? (
                similarVehicles.map((similarVehicle, index) => (
                  <div className="col-lg-4 col-md-6 mb-4" key={index}>
                    <div className="card shadow-sm">
                      <img
                        src={similarVehicle.imageUrls[0]}
                        alt={`Véhicule ${similarVehicle.marque} ${similarVehicle.modele}`}
                        className="card-img-top"
                        style={{ height: "200px", objectFit: "cover" }}
                      />
                      <div className="card-body">
                        <h5 className="card-title">{`${similarVehicle.marque} ${similarVehicle.modele}`}</h5>
                        <p className="card-text">{similarVehicle.annee}</p>
                        <Link to={`/vehicules/${similarVehicle.slug}`} className="btn btn-primary">
                          Voir Détails
                        </Link>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p>Aucun véhicule similaire trouvé.</p>
              )}
            </div>
          </div>
        </div>
      </main>
      <footer style={{ backgroundColor: "#0C1E47" }} className="text-white pt-5 pb-3 mt-5 border-top">
        <div className="container">
          <div className="row gy-4">
            {/* Logo & Slogan */}
            <div className="col-md-4">
              <img src="/BRIDGE BLANC.png" alt="THE BRIDGE logo" width="180" className="mb-3" />
              <p style={{ color: "#F7931E", fontWeight: 500 }}>Let’s connect the world together</p>
              <p className="small text-white-50">THE BRIDGE est votre partenaire de confiance pour l’achat de véhicules fiables à travers le monde.</p>
            </div>
            {/* Liens rapides */}
            <div className="col-md-4">
              <h6 className="text-uppercase fw-semibold mb-3">Navigation</h6>
              <ul className="list-unstyled">
                <li><a href="/" className="text-white-50 text-decoration-none">Accueil</a></li>
                <li><a href="/catalogue" className="text-white-50 text-decoration-none">Nos véhicules</a></li>
                <li><a href="/contact" className="text-white-50 text-decoration-none">Contact</a></li>
                <li><a href="/mentions-legales" className="text-white-50 text-decoration-none">Mentions légales</a></li>
              </ul>
            </div>
            {/* Infos de contact */}
            <div className="col-md-4">
              <h6 className="text-uppercase fw-semibold mb-3">Nous contacter</h6>
              <p className="text-white-50 small mb-1"><i className="bi bi-geo-alt me-2"></i>Lomé, Togo</p>
              <p className="text-white-50 small mb-1"><i className="bi bi-envelope me-2"></i>contact@thebridgeofworld.com</p>
              <p className="text-white-50 small mb-0"><i className="bi bi-phone me-2"></i>+228 79 30 94 00/+228 99 41 00 41 </p>
            </div>
          </div>
          {/* Bas de page */}
          <div className="text-center mt-4 pt-3 border-top border-secondary text-white-50 small">
            © 2025 THE BRIDGE — Tous droits réservés
          </div>
        </div>
      </footer>

      {showVideoModal && (
        <div className="modal show fade d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Vidéo du véhicule</h5>
                <button type="button" className="btn-close" onClick={() => setShowVideoModal(false)}></button>
              </div>
              <div className="modal-body">
                <video controls className="w-100 rounded" autoPlay>
                  <source src={vehicle.videoUrl} type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              </div>
            </div>
          </div>
        </div>
      )}

      {show360Modal && (
        <div className="modal show fade d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Vue interactive 360°</h5>
                <button type="button" className="btn-close" onClick={() => setShow360Modal(false)}></button>
              </div>
              <div className="modal-body text-center">
                <img loading="lazy" src={mainImage} alt="Vue 360" className="img-fluid rounded" style={{ maxHeight: "400px", animation: "spin 15s linear infinite" }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(360deg); }
        }
      `}</style>

      <WhatsAppButton vehicle={vehicle} />
    </>
  );
}
